import { toast } from "~/environment/toast-service";
import { withActionContext, write } from "./write";
import { reindexNotification } from "./notification";
import * as actions from "libs/actions";

/* -------------------------------------------------------------------------------------------------
 * resolveThreadRequest
 * -------------------------------------------------------------------------------------------------
 */

export const resolveThreadRequest = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      requestId: string;
      resolvedBy: {
        sentAt: string;
        timelineOrder: string;
      };
    },
  ) => {
    const [request] = await context.environment.loader.getRecord("thread_request", props.requestId);

    if (!request) {
      throw new actions.InvalidActionStateError("[resolveThreadRequest] request not found", {
        errorData: { props },
      });
    }

    return {
      forward: async () => {
        const action = await actions.threadRequest.updateThreadRequestLastResolvedAt.action(
          {
            threadId: props.threadId,
            requestId: props.requestId,
            lastResolvedAt: {
              sent_at: props.resolvedBy.sentAt,
              timeline_order: props.resolvedBy.timelineOrder,
            },
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Request resolved`,
            });

            reindexNotification(environment, { threadId: request.thread_id });
          },
        });
      },
      backward: async () => {
        const action = await actions.threadRequest.updateThreadRequestLastResolvedAt.action(
          {
            threadId: props.threadId,
            requestId: props.requestId,
            lastResolvedAt:
              request.last_resolved_at && request.last_resolved_at_timeline_order ?
                {
                  sent_at: request.last_resolved_at,
                  timeline_order: request.last_resolved_at_timeline_order,
                }
              : null,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Request un-resolved`,
            });

            reindexNotification(environment, { threadId: request.thread_id });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * unResolveThreadRequest
 * -------------------------------------------------------------------------------------------------
 */

export const unResolveThreadRequest = withActionContext(
  async (
    context,
    environment,
    props: {
      requestId: string;
      threadId: string;
      unResolvedAtMessageId: string;
    },
  ) => {
    const [request] = await context.environment.loader.getRecord("thread_request", props.requestId);

    if (!request) {
      throw new actions.InvalidActionStateError("[unResolveThreadRequest] request not found", {
        errorData: { props },
      });
    }

    return {
      forward: async () => {
        toast("vanilla", {
          subject: `Unresolving request...`,
        });

        const [messages] = await context.environment.loader.getMessages({ thread_id: props.threadId });

        let foundMessage = false;
        let unResolvedAtMessage = null;

        // We want to mark the thread as resolved up until the unResolvedAtMessageId message,
        // so find the message right before this message.
        for (const message of messages.reverse()) {
          if (message.id === props.unResolvedAtMessageId) {
            foundMessage = true;
            continue;
          }

          if (foundMessage) {
            unResolvedAtMessage = message;
            break;
          }
        }

        const action = await actions.threadRequest.updateThreadRequestLastResolvedAt.action(
          {
            threadId: props.threadId,
            requestId: props.requestId,
            lastResolvedAt:
              !unResolvedAtMessage ? null : (
                {
                  sent_at: unResolvedAtMessage.sent_at,
                  timeline_order: unResolvedAtMessage.timeline_order,
                }
              ),
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite() {
            toast("vanilla", {
              subject: `Unresolving request...Done!`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const action = await actions.threadRequest.updateThreadRequestLastResolvedAt.action(
          {
            threadId: props.threadId,
            requestId: props.requestId,
            lastResolvedAt:
              request.last_resolved_at && request.last_resolved_at_timeline_order ?
                {
                  sent_at: request.last_resolved_at,
                  timeline_order: request.last_resolved_at_timeline_order,
                }
              : null,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Change undone`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
    };
  },
);
